import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Video } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, ReferenceLine } from 'recharts';
import { cn } from '@/lib/utils';

interface VideoData {
  id: string;
  name: string;
  isMain?: boolean;
}

interface HookTrackRetentionCardProps {
  videos: VideoData[];
  retentionData: any[];
  winnerVideoId?: string | null;
  hasData?: boolean;
  pitchTime?: number; // Tempo do pitch em segundos
  maxVideoDuration?: number; // Duração máxima em segundos para o eixo X
}

// Paleta oficial HookTrack PRD (sem roxo)
const HOOK_COLORS = [
  '#0A84FF', // Intro 1 - Azul Host PRD
  '#8A2BE2', // Intro 2 - Violeta
  '#34C759', // Intro 3 - Verde PRD
  '#FF9500', // Intro 4 - Laranja PRD
];

// Tooltip mostrando TODOS os vídeos
const PremiumHookTooltip = ({ active, payload, label, videos, retentionData }: any) => {
  useLanguage();
  if (!active || !payload?.length) return null;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  // Encontrar o ponto de dados para este tempo
  const dataPoint = retentionData.find((d: any) => d.time === label);
  if (!dataPoint) return null;

  return (
    <div
      className="animate-fade-in"
      style={{
        background: '#FFFFFF',
        border: '1px solid #E4EBFF',
        borderRadius: '14px',
        padding: '16px 20px',
        boxShadow: '0px 14px 28px rgba(0, 0, 0, 0.03)',
      }}
    >
      <div className="text-[14px] font-semibold text-[#1C2434] mb-2">
        {formatTime(label)}
      </div>
      <div className="h-px bg-[#EDF1FF] mb-3" style={{ margin: '8px 0 10px 0' }} />

      {videos.map((video: any, index: number) => {
        const retention = dataPoint[video.id];
        if (retention === undefined || retention === null) return null;

        return (
          <div key={video.id} className="flex items-center justify-between gap-6 mb-1.5 last:mb-0">
            <div className="flex items-center gap-2">
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: HOOK_COLORS[index % HOOK_COLORS.length] }}
              />
              <span className="text-[13px] text-[#5A6475]">
                {video.name}
              </span>
            </div>
            <span className="text-[14px] font-medium text-[#344054]">
              {retention.toFixed(1)}%
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Componente para o label do PITCH
const PitchLabel = ({ viewBox }: any) => {
  useLanguage();
  const { x } = viewBox;
  return (
    <g>
      <foreignObject x={x - 24} y={4} width={48} height={24}>
        <div
          style={{
            background: '#FFF6E2',
            color: '#C27C1E',
            padding: '4px 8px',
            borderRadius: '8px',
            fontSize: '11px',
            fontWeight: 600,
            textAlign: 'center',
            boxShadow: '0 2px 6px rgba(255, 163, 53, 0.25)',
          }}
        >
          {translateInterfaceText("PITCH")}</div>
      </foreignObject>
    </g>
  );
};

export function HookTrackRetentionCard({
  videos,
  retentionData,
  winnerVideoId,
  hasData = true,
  pitchTime,
  maxVideoDuration
}: HookTrackRetentionCardProps) {
  useLanguage();
  // Encontrar o vídeo com MAIOR RETENÇÃO (soma de todos os pontos)
  const bestVideoId = React.useMemo(() => {
    if (videos.length === 0) return '';

    const videoScores = videos.map(video => {
      const totalRetention = retentionData.reduce((sum, point) => {
        const value = point[video.id];
        return sum + (typeof value === 'number' ? value : 0);
      }, 0);
      return { id: video.id, score: totalRetention };
    });

    videoScores.sort((a, b) => b.score - a.score);
    return videoScores[0]?.id || videos[0].id;
  }, [videos, retentionData]);

  const bestVideo = videos.find(v => v.id === bestVideoId);

  // Filtrar dados - mostrar apenas linha do melhor vídeo
  // Preencher gaps com último valor conhecido
  const filteredData: any[] = [];
  let lastValue: number | undefined = undefined;

  retentionData.forEach(point => {
    const value = point[bestVideoId];

    // Se tem valor, usa ele e atualiza o último
    if (value !== undefined && value !== null) {
      lastValue = value;
      filteredData.push({ time: point.time, retention: value });
    }
    // Se não tem valor mas tem último conhecido, usa o último
    else if (lastValue !== undefined) {
      filteredData.push({ time: point.time, retention: lastValue });
    }
    // Se não tem nada, pula (não adiciona)
  });

  console.log('🏆 Melhor vídeo:', bestVideo?.name, bestVideoId);
  console.log('📈 Dados filtrados:', filteredData);

  if (!hasData) {
    return (
      <div className="p-8">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center mb-4">
            <Video className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {translateInterfaceText("Ainda não há dados de retenção")}</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            {translateInterfaceText("Assim que o teste começar a receber visualizações, você verá a comparação de retenção entre as introduções aqui.")}</p>
        </div>
      </div>
    );
  }

  return (
    <div>

      {/* Chart Premium - AreaChart igual TrafficRetentionChart */}
      <div className="w-full h-[500px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={filteredData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="gradient-retention" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0A84FF" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#0A84FF" stopOpacity={0} />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#F2F2F7"
              strokeOpacity={0.35}
              vertical={false}
            />

            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#8A94A6', fontSize: 12 }}
              domain={maxVideoDuration ? [0, maxVideoDuration] : undefined}
              tickFormatter={(value) => {
                const mins = Math.floor(value / 60);
                const secs = value % 60;
                return mins > 0 ? `${mins}m` : `${secs}s`;
              }}
            />

            <YAxis
              domain={[0, 100]}
              type="number"
              scale="linear"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#8A94A6', fontSize: 12 }}
              tickFormatter={(value) => `${value}%`}
              width={50}
              ticks={[0, 20, 40, 60, 80, 100]}
            />

            <Tooltip content={<PremiumHookTooltip videos={videos} retentionData={retentionData} />} />

            {/* Marcador PITCH Premium */}
            {pitchTime && (
              <ReferenceLine
                x={pitchTime}
                stroke="#FFA335"
                strokeWidth={2}
                strokeDasharray="6 4"
                style={{ filter: 'drop-shadow(0px 0px 4px rgba(255, 163, 53, 0.32))' }}
                label={<PitchLabel />}
              />
            )}

            {/* Apenas UMA linha */}
            <Area
              type="monotone"
              dataKey="retention"
              name={bestVideo?.name || 'Retenção'}
              stroke="#0A84FF"
              strokeWidth={3}
              fill="url(#gradient-retention)"
              dot={false}
              activeDot={{
                r: 7,
                fill: '#0A84FF',
                stroke: '#FFFFFF',
                strokeWidth: 3
              }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
